import * as THREE from 'three';
import { drawnTexture, seeded } from './kit.js';

/**
 * A parked car, drawn from its collision box, and what being put into it does
 * to it.
 *
 * The collision sees a `box` — the same shape the train is — and this builds a
 * car inside that footprint, bonnet towards +z, so the face a fighter is sent
 * into from the floor is the front. Which part of the car takes the body comes
 * from the contact the surface event carries (`normal`, pointing out of the car
 * at the fighter, and `at`): the front is the bonnet and the windscreen above
 * it, the sides are the door glass, the back is the rear window.
 *
 * The damage is **state first, picture second**. `car.damage` is plain data —
 * how deep the bonnet is dented, which glass is cracked or gone, how long the
 * alarm has left — and the unit suite reads it in `node`, where there is no
 * canvas to draw a crack on. The picture follows the state: the bonnet's own
 * vertices are pushed in, a crack pattern is painted onto an overlay over the
 * glass, a shattered pane gets a hole and glass on the floor, and the
 * indicators flash for as long as the alarm runs.
 *
 * Cumulative within a round and put right by `reset()`, which the host calls
 * at every intro — a fight is one round, and the next one is in a garage
 * nobody has fought in.
 */

export const GLASS = Object.freeze(['intact', 'cracked', 'shattered']);

/** Which face of the car a contact normal (out of the car) is on. */
export function faceOf(normal) {
  if (!normal) return 'front';
  if (normal.z > 0.5) return 'front';
  if (normal.z < -0.5) return 'rear';
  return normal.x > 0 ? 'right' : 'left';
}

export function createCar(kit, surface, { paint = '#7d1f22', palette = {}, damage: rules = {} } = {}) {
  const { center, halfX, halfZ } = surface;
  const width = halfX * 2;
  const height = surface.height ?? 1.45;
  const front = halfZ;
  const tone = (key, fallback) => new THREE.Color(palette[key] ?? fallback);

  const group = new THREE.Group();
  group.name = `car-${surface.id}`;
  group.position.set(center.x, 0, center.z);

  const body = kit.material({ color: new THREE.Color(paint), roughness: 0.32, metalness: 0.55 });
  const trim = kit.material({ color: '#151618', roughness: 0.6, metalness: 0.3 });
  const chrome = kit.material({ color: tone('chrome', '#a7adb3'), roughness: 0.25, metalness: 0.9 });
  const tyre = kit.material({ color: tone('tyre', '#18191b'), roughness: 0.95 });
  const glass = kit.material({
    color: tone('glass', '#25313a'),
    roughness: 0.05,
    metalness: 0.4,
    transparent: true,
    opacity: 0.62,
    side: THREE.DoubleSide,
  });
  const at = (geometry, mat, options) => kit.place(geometry, mat, { parent: group, ...options });

  // --- body ------------------------------------------------------------------
  const sill = 0.18;
  const belt = 0.8;
  const hoodLength = 1.15;
  const hoodFront = front - 0.06;
  const hoodBack = hoodFront - hoodLength;
  // The body to the beltline behind the bonnet; under the bonnet it stops
  // short, as an engine bay does, so a dent pushed into the bonnet shows
  // rather than sinking into solid paint. The wings and the nose close the
  // sides of the bay.
  const bay = 0.3;
  const behind = hoodBack + halfZ;
  at(new THREE.BoxGeometry(width, belt - sill, behind), body, { y: (sill + belt) / 2, z: -halfZ + behind / 2, name: 'car-body' });
  const ahead = front - hoodBack;
  at(new THREE.BoxGeometry(width, belt - bay - sill, ahead), body, {
    y: (sill + belt - bay) / 2,
    z: hoodBack + ahead / 2,
    name: 'car-body-front',
  });
  for (const sign of [-1, 1]) {
    at(new THREE.BoxGeometry(width * 0.03, bay, ahead), body, {
      x: sign * (width / 2 - width * 0.015),
      y: belt - bay / 2,
      z: hoodBack + ahead / 2,
      name: 'car-wing',
    });
  }
  at(new THREE.BoxGeometry(width, bay, 0.06), body, { y: belt - bay / 2, z: front - 0.03, name: 'car-nose' });
  const hoodGeometry = new THREE.PlaneGeometry(width * 0.95, hoodLength, 14, 10);
  const hoodRest = Float32Array.from(hoodGeometry.getAttribute('position').array);
  const hood = at(hoodGeometry, body, {
    y: belt + 0.012,
    z: (hoodFront + hoodBack) / 2,
    rotX: -Math.PI / 2,
    name: 'car-hood',
  });

  // The cabin: windscreen up from the back of the bonnet, a roof, a rear window
  // down to the boot lid.
  const screenRun = 0.78;
  const rearRun = 0.62;
  const roofLength = Math.max(0.6, halfZ * 2 - hoodLength - screenRun - rearRun - 0.75);
  const roofFront = hoodBack - screenRun;
  const roofBack = roofFront - roofLength;
  const bootStart = roofBack - rearRun;
  const cabinWidth = width * 0.86;
  const rise = height - belt;

  const pane = (from, to, name) => {
    // A pane from one edge (y, z) to another, across the cabin.
    const length = Math.hypot(to.y - from.y, to.z - from.z);
    const tilt = Math.atan2(to.z - from.z, to.y - from.y);
    return at(new THREE.PlaneGeometry(cabinWidth, length), glass, {
      y: (from.y + to.y) / 2,
      z: (from.z + to.z) / 2,
      rotX: tilt,
      name,
      casts: false,
    });
  };
  const windscreen = pane({ y: belt, z: hoodBack }, { y: height, z: roofFront }, 'car-windscreen');
  const rearWindow = pane({ y: height, z: roofBack }, { y: belt + 0.04, z: bootStart }, 'car-rear-window');
  at(new THREE.BoxGeometry(cabinWidth, 0.07, roofLength + 0.04), body, {
    y: height - 0.035,
    z: (roofFront + roofBack) / 2,
    name: 'car-roof',
  });
  at(new THREE.BoxGeometry(width * 0.95, 0.04, bootStart + halfZ - 0.05), body, {
    y: belt + 0.02,
    z: (bootStart - halfZ + 0.05) / 2,
    name: 'car-boot',
  });

  // Door glass, one pane a side, and the pillars that hold the roof up.
  const sideLength = hoodBack - bootStart;
  const sides = {};
  for (const [key, sign] of [['left', -1], ['right', 1]]) {
    sides[key] = at(new THREE.PlaneGeometry(sideLength * 0.92, rise - 0.08), glass, {
      x: sign * (cabinWidth / 2),
      y: belt + (rise - 0.08) / 2 + 0.02,
      z: (hoodBack + bootStart) / 2,
      rotY: Math.PI / 2,
      name: `car-window-${key}`,
      casts: false,
    });
    for (const z of [hoodBack - screenRun / 2, (roofFront + roofBack) / 2, roofBack - rearRun / 2]) {
      at(new THREE.BoxGeometry(0.06, rise, 0.07), trim, { x: sign * (cabinWidth / 2), y: belt + rise / 2, z, name: 'car-pillar', casts: false });
    }
  }

  // Wheels, bumpers, a grille.
  const wheel = new THREE.CylinderGeometry(0.32, 0.32, 0.22, 18);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      at(wheel.clone(), tyre, { x: sx * (halfX - 0.1), y: 0.32, z: sz * (halfZ - 0.78), rotZ: Math.PI / 2, name: 'car-wheel' });
    }
  }
  kit.keep(wheel);
  at(new THREE.BoxGeometry(width + 0.04, 0.16, 0.12), chrome, { y: 0.36, z: front - 0.02, name: 'car-bumper-front' });
  at(new THREE.BoxGeometry(width + 0.04, 0.16, 0.12), chrome, { y: 0.36, z: -front + 0.02, name: 'car-bumper-rear' });
  at(new THREE.BoxGeometry(width * 0.42, 0.16, 0.03), trim, { y: 0.6, z: front + 0.005, name: 'car-grille', casts: false });

  // The lights: headlamps and tail lamps, and the four indicators the alarm
  // flashes. Their own materials, so one car's alarm does not set off the other.
  const glow = (color, intensity) =>
    kit.material({ color, emissive: color, emissiveIntensity: intensity, roughness: 0.3 });
  const headlamp = glow(tone('headlight', '#fff6dc'), 0.35);
  const taillamp = glow(tone('taillight', '#c2201c'), 0.4);
  const indicator = glow(tone('indicator', '#ffa31a'), 0.15);
  for (const sx of [-1, 1]) {
    at(new THREE.BoxGeometry(0.34, 0.12, 0.04), headlamp, { x: sx * (halfX - 0.3), y: 0.64, z: front + 0.005, name: 'car-headlamp', casts: false });
    at(new THREE.BoxGeometry(0.34, 0.1, 0.04), taillamp, { x: sx * (halfX - 0.3), y: 0.66, z: -front - 0.005, name: 'car-taillamp', casts: false });
    for (const sz of [-1, 1]) {
      at(new THREE.BoxGeometry(0.1, 0.07, 0.05), indicator, {
        x: sx * (halfX - 0.06),
        y: 0.64,
        z: sz * (front + 0.006),
        name: 'car-indicator',
        casts: false,
      });
    }
  }

  // --- the damage ------------------------------------------------------------

  const damage = {
    /** Metres the bonnet is pushed in at its deepest. */
    hood: 0,
    windscreen: 'intact',
    left: 'intact',
    right: 'intact',
    rear: 'intact',
    /** Seconds of alarm left. */
    alarm: 0,
    hits: 0,
  };
  const dents = [];
  const panes = { windscreen, left: sides.left, right: sides.right, rear: rearWindow };
  const overlays = {};
  const random = seeded(surface.id.length * 131 + 7);

  /** Glass on the floor. Preallocated, shown when a pane goes. */
  const shardCount = 48;
  const shards = new THREE.InstancedMesh(
    kit.keep(new THREE.TetrahedronGeometry(0.035)),
    kit.material({ color: '#cfe3ee', roughness: 0.1, metalness: 0.2, transparent: true, opacity: 0.8 }),
    shardCount
  );
  shards.name = 'car-glass-shards';
  shards.count = 0;
  group.add(shards);

  function dentHood(localX, amount) {
    const position = hoodGeometry.getAttribute('position');
    const spread = rules.spread ?? 0.32;
    // In the bonnet's own plane: x across, y towards the windscreen. A body
    // folded back over the front lands its shoulders and head on the back half.
    const centre = { x: localX, y: hoodLength * 0.18 };
    dents.push({ ...centre, amount });
    let deepest = 0;
    for (let i = 0; i < position.count; i++) {
      const x = hoodRest[i * 3];
      const y = hoodRest[i * 3 + 1];
      let depth = 0;
      for (const dent of dents) {
        const d2 = (x - dent.x) ** 2 + (y - dent.y) ** 2;
        depth += dent.amount * Math.exp(-d2 / (2 * spread * spread));
      }
      depth = Math.min(depth, rules.dentMax ?? 0.26);
      deepest = Math.max(deepest, depth);
      position.setZ(i, hoodRest[i * 3 + 2] - depth);
    }
    position.needsUpdate = true;
    hoodGeometry.computeVertexNormals();
    damage.hood = deepest;
  }

  /** Paints the cracks a pane's state calls for, from where it was struck. */
  function paintGlass(key, state, origin) {
    const mesh = panes[key];
    if (!mesh) return;
    mesh.material = state === 'shattered' ? shatteredGlass() : mesh.material;
    const texture = drawnTexture(256, 128, (ctx, w, h, rnd) => {
      ctx.clearRect(0, 0, w, h);
      const ox = origin.u * w;
      const oy = origin.v * h;
      if (state === 'shattered') {
        // The hole: a jagged dark shape where the glass is gone and the cabin
        // shows through.
        ctx.fillStyle = 'rgba(8,9,11,0.92)';
        ctx.beginPath();
        for (let k = 0; k < 14; k++) {
          const angle = (k / 14) * Math.PI * 2;
          const r = (0.12 + rnd() * 0.12) * h;
          ctx.lineTo(ox + Math.cos(angle) * r * 1.6, oy + Math.sin(angle) * r);
        }
        ctx.closePath();
        ctx.fill();
      }
      ctx.strokeStyle = palette.crack ?? '#e6eef4';
      ctx.globalAlpha = 0.85;
      const spokes = state === 'shattered' ? 22 : 12;
      for (let k = 0; k < spokes; k++) {
        const angle = (k / spokes) * Math.PI * 2 + rnd() * 0.3;
        const reach = (0.3 + rnd() * 0.7) * w * (state === 'shattered' ? 0.7 : 0.4);
        ctx.lineWidth = 1 + rnd();
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        let x = ox;
        let y = oy;
        for (let seg = 1; seg <= 5; seg++) {
          x = ox + Math.cos(angle + (rnd() - 0.5) * 0.25) * (reach * seg) / 5;
          y = oy + Math.sin(angle + (rnd() - 0.5) * 0.25) * (reach * seg) / 5;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      // The rings that make it read as a strike rather than a scribble.
      for (let ring = 1; ring <= (state === 'shattered' ? 4 : 2); ring++) {
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(ox, oy, ring * 0.07 * w, ring * 0.1 * h, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }, { seed: (damage.hits + 1) * 17 + key.length });
    if (!texture) return;
    kit.keep(texture);
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    let overlay = overlays[key];
    if (!overlay) {
      overlay = new THREE.Mesh(
        mesh.geometry,
        kit.material({ map: texture, transparent: true, depthWrite: false, side: THREE.DoubleSide, roughness: 0.2, metalness: 0 })
      );
      overlay.name = `${mesh.name}-cracks`;
      overlay.renderOrder = 2;
      mesh.add(overlay);
      overlay.position.z = 0.004;
      overlays[key] = overlay;
    } else {
      overlay.material.map = texture;
      overlay.material.needsUpdate = true;
    }
    overlay.visible = true;
  }

  let broken = null;
  function shatteredGlass() {
    broken ??= kit.material({ color: tone('glass', '#25313a'), roughness: 0.3, metalness: 0.2, transparent: true, opacity: 0.35, side: THREE.DoubleSide });
    return broken;
  }

  /** Glass on the floor in front of a pane that just went. */
  function scatter(x, z) {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const start = shards.count;
    const added = Math.min(16, shardCount - start);
    for (let i = 0; i < added; i++) {
      e.set(random() * 6, random() * 6, random() * 6);
      q.setFromEuler(e);
      const r = 0.15 + random() * 0.75;
      const a = random() * Math.PI * 2;
      m.compose(new THREE.Vector3(x + Math.cos(a) * r, 0.02, z + Math.sin(a) * r * 0.6), q, new THREE.Vector3(1, 0.3, 1));
      shards.setMatrixAt(start + i, m);
    }
    shards.count = start + added;
    shards.instanceMatrix.needsUpdate = true;
  }

  const worse = (state) => (state === 'intact' ? 'cracked' : 'shattered');

  return {
    id: surface.id,
    object3D: group,
    damage,
    hood,
    panes,

    /**
     * A body arrived at this car. Returns which face took it.
     *
     * The front dents the bonnet every time and breaks the windscreen in two
     * steps — cracked, then gone — and a **finish** goes straight through:
     * the windscreen shatters whatever it was. The sides and back break their
     * glass the same way. Any of it sets the alarm off.
     */
    hit({ at: where = null, normal = null, knockout = false } = {}) {
      const face = faceOf(normal);
      const localX = where ? Math.max(-halfX * 0.8, Math.min(halfX * 0.8, where.x - center.x)) : 0;
      const localZ = where ? Math.max(-halfZ, Math.min(halfZ, where.z - center.z)) : 0;
      damage.hits += 1;
      damage.alarm = rules.alarm ?? 9;

      if (face === 'front') {
        dentHood(localX, (rules.dent ?? 0.11) * (knockout ? 1.8 : 1));
        damage.windscreen = knockout ? 'shattered' : worse(damage.windscreen);
        // `v` is canvas rows, top down; the canvas's top is the pane's top
        // edge, at the roof. A head through the windscreen goes in low, where
        // it meets the bonnet.
        paintGlass('windscreen', damage.windscreen, { u: 0.5 + localX / (cabinWidth || 1), v: 0.8 });
        if (damage.windscreen === 'shattered') scatter(localX, front + 0.4);
        return face;
      }
      const key = face === 'rear' ? 'rear' : face;
      damage[key] = knockout ? 'shattered' : worse(damage[key]);
      const u = face === 'rear' ? 0.5 - localX / (cabinWidth || 1) : 0.5 + (face === 'right' ? -1 : 1) * localZ / (sideLength || 1);
      paintGlass(key, damage[key], { u: Math.max(0.1, Math.min(0.9, u)), v: 0.45 });
      if (damage[key] === 'shattered') {
        if (face === 'rear') scatter(localX, -front - 0.4);
        else scatter((face === 'right' ? 1 : -1) * (halfX + 0.4), localZ);
      }
      return face;
    },

    /** The alarm: indicators flash and the headlamps pulse until it runs out. */
    update(dt) {
      if (damage.alarm <= 0) return;
      damage.alarm = Math.max(0, damage.alarm - dt);
      const on = damage.alarm > 0 && Math.floor(damage.alarm * (rules.blink ?? 2.2) * 2) % 2 === 0;
      indicator.emissiveIntensity = on ? 3.2 : 0.15;
      headlamp.emissiveIntensity = on ? 1.8 : 0.35;
    },

    /** As it was parked. */
    reset() {
      dents.length = 0;
      const position = hoodGeometry.getAttribute('position');
      position.array.set(hoodRest);
      position.needsUpdate = true;
      hoodGeometry.computeVertexNormals();
      for (const key of Object.keys(panes)) {
        damage[key] = 'intact';
        panes[key].material = glass;
        if (overlays[key]) overlays[key].visible = false;
      }
      damage.hood = 0;
      damage.alarm = 0;
      damage.hits = 0;
      shards.count = 0;
      indicator.emissiveIntensity = 0.15;
      headlamp.emissiveIntensity = 0.35;
    },
  };
}
